import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { NavItem, SidebarProps, SortField } from '../types';

interface NavItemComponentProps {
  item: NavItem;
  level: number;
  onNavigate?: () => void;
}

function NavItemComponent({ item, level, onNavigate }: NavItemComponentProps) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(() => {
    // Auto-expand if current path is within this section
    if (item.children.length > 0) {
      return (
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + '/')
      );
    }
    return false;
  });

  const hasChildren = item.children.length > 0;
  const paddingLeft = `${level * 0.75}rem`;

  if (item.navStyle === 'heading' && hasChildren) {
    // Static section heading — always expanded, not collapsible
    return (
      <div>
        <div
          className="mt-6 mb-2 text-xs font-bold uppercase tracking-wider text-[var(--color-site-header,#ea580c)] dark:text-[var(--color-site-header-light,#fb923c)]"
          style={{ paddingLeft }}
        >
          {item.title}
        </div>
        <div>
          {item.children.map((child) => (
            <NavItemComponent
              key={child.path}
              item={child}
              level={level}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!item.file && hasChildren) {
    // Directory without README - render as collapsible section header
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800"
          style={{ paddingLeft }}
        >
          <span>{item.title}</span>
          <svg
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
        {isOpen && (
          <div className="mt-1">
            {item.children.map((child) => (
              <NavItemComponent
                key={child.path}
                item={child}
                level={level + 1}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <NavLink
        to={item.path}
        onClick={onNavigate}
        className={({ isActive: active }) =>
          `flex items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors ${
            active
              ? 'bg-docs-100 font-medium text-docs-700 dark:bg-docs-900/50 dark:text-docs-300'
              : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
          }`
        }
        style={{ paddingLeft }}
      >
        <span>{item.title}</span>
        {hasChildren && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="rounded p-0.5 hover:bg-gray-300 dark:hover:bg-gray-700"
          >
            <svg
              className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </NavLink>
      {hasChildren && isOpen && (
        <div className="mt-1">
          {item.children.map((child) => (
            <NavItemComponent
              key={child.path}
              item={child}
              level={level + 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Define group order - groups appear in this order
const GROUP_ORDER = ['Company', 'Products', 'Clients', 'Reference', 'Other'];

interface GroupedItems {
  [group: string]: NavItem[];
}

function GroupHeader({ name, isFirst }: { name: string; isFirst: boolean }) {
  return (
    <div
      className={`${isFirst ? 'mt-2' : 'mt-8'} mb-2 text-xs font-bold uppercase tracking-wider text-[var(--color-site-header,#ea580c)] dark:text-[var(--color-site-header-light,#fb923c)]`}
    >
      {name}
    </div>
  );
}

// Recursively filter out hidden items from navigation
function filterHiddenItems(items: NavItem[]): NavItem[] {
  return items
    .filter((item) => !item.hidden)
    .map((item) => ({
      ...item,
      children: filterHiddenItems(item.children),
    }));
}

// Sort items by the specified field
function sortItems(items: NavItem[], sortField: SortField): NavItem[] {
  return [...items].sort((a, b) => {
    let aVal: string;
    let bVal: string;

    switch (sortField) {
      case 'firstName':
        aVal = a.firstName || a.title;
        bVal = b.firstName || b.title;
        break;
      case 'lastName':
        aVal = a.lastName || a.title;
        bVal = b.lastName || b.title;
        break;
      default:
        aVal = a.title;
        bVal = b.title;
    }

    return aVal.localeCompare(bVal);
  });
}

// Sort toggle component
function SortToggle({
  sortField,
  onChange,
}: {
  sortField: SortField;
  onChange: (field: SortField) => void;
}) {
  return (
    <div className="mb-4 flex items-center gap-2 text-xs">
      <span className="text-gray-500 dark:text-gray-400">Sort by:</span>
      <button
        onClick={() => onChange('firstName')}
        className={`rounded px-2 py-1 transition-colors ${
          sortField === 'firstName'
            ? 'bg-docs-100 text-docs-700 dark:bg-docs-900/50 dark:text-docs-300'
            : 'text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-800'
        }`}
      >
        First
      </button>
      <button
        onClick={() => onChange('lastName')}
        className={`rounded px-2 py-1 transition-colors ${
          sortField === 'lastName'
            ? 'bg-docs-100 text-docs-700 dark:bg-docs-900/50 dark:text-docs-300'
            : 'text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-800'
        }`}
      >
        Last
      </button>
    </div>
  );
}

export function Sidebar({
  navigation,
  onNavigate,
  enableSort = false,
  defaultSortField = 'firstName',
  sortStorageKey = 'sidebar-sort-preference',
}: SidebarProps) {
  // Load sort preference from localStorage
  const [sortField, setSortField] = useState<SortField>(() => {
    if (!enableSort) return defaultSortField;
    if (typeof window === 'undefined') return defaultSortField;
    const stored = localStorage.getItem(sortStorageKey);
    if (stored === 'firstName' || stored === 'lastName' || stored === 'title') {
      return stored;
    }
    return defaultSortField;
  });

  // Persist sort preference to localStorage
  useEffect(() => {
    if (enableSort && typeof window !== 'undefined') {
      localStorage.setItem(sortStorageKey, sortField);
    }
  }, [sortField, sortStorageKey, enableSort]);

  const handleSortChange = (field: SortField) => {
    setSortField(field);
  };
  // Filter out hidden items (e.g., completed OPORDs)
  const visibleItems = filterHiddenItems(navigation.items);

  // Flatten the root item if it exists (the root README)
  const displayItems =
    visibleItems.length === 1 && visibleItems[0].path === '/'
      ? [{ ...visibleItems[0], children: [] }, ...visibleItems[0].children]
      : visibleItems;

  // Separate root item (path === '/') from other items
  const rootItem = displayItems.find((item) => item.path === '/');
  const otherItems = displayItems.filter((item) => item.path !== '/');

  // Group items by their group field
  const grouped: GroupedItems = {};
  const ungrouped: NavItem[] = [];

  for (const item of otherItems) {
    if (item.group) {
      if (!grouped[item.group]) {
        grouped[item.group] = [];
      }
      grouped[item.group].push(item);
    } else {
      ungrouped.push(item);
    }
  }

  // Check if any items have groups
  const hasGroups = Object.keys(grouped).length > 0;

  // If no items have groups, render flat list (backwards compatible)
  // Apply sorting if enabled — sort children of heading items too
  if (!hasGroups) {
    let itemsToRender = displayItems;
    if (enableSort) {
      const headings = displayItems
        .filter((item) => item.navStyle === 'heading')
        .map((item) => ({
          ...item,
          children: sortItems(item.children, sortField),
        }));
      const sortable = displayItems.filter(
        (item) => item.navStyle !== 'heading'
      );
      itemsToRender = [...sortItems(sortable, sortField), ...headings];
    }
    return (
      <nav className="space-y-1">
        {enableSort && (
          <SortToggle sortField={sortField} onChange={handleSortChange} />
        )}
        {itemsToRender.map((item) => (
          <NavItemComponent
            key={item.path}
            item={item}
            level={0}
            onNavigate={onNavigate}
          />
        ))}
      </nav>
    );
  }

  // Track if we've rendered any groups yet (for isFirst prop)
  let groupsRendered = 0;

  // Render grouped items with root item first
  return (
    <nav className="space-y-1">
      {/* Render root item first, without a group */}
      {rootItem && (
        <NavItemComponent
          key={rootItem.path}
          item={rootItem}
          level={0}
          onNavigate={onNavigate}
        />
      )}
      {/* Render groups in defined order */}
      {GROUP_ORDER.map((groupName) => {
        const groupItems = grouped[groupName];
        if (!groupItems || groupItems.length === 0) return null;
        const isFirst = groupsRendered === 0;
        groupsRendered++;
        return (
          <div key={groupName}>
            <GroupHeader name={groupName} isFirst={isFirst} />
            {groupItems.map((item) => (
              <NavItemComponent
                key={item.path}
                item={item}
                level={1}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        );
      })}
      {/* Render any groups not in GROUP_ORDER */}
      {Object.keys(grouped)
        .filter((g) => !GROUP_ORDER.includes(g))
        .map((groupName) => {
          const groupItems = grouped[groupName];
          const isFirst = groupsRendered === 0;
          groupsRendered++;
          return (
            <div key={groupName}>
              <GroupHeader name={groupName} isFirst={isFirst} />
              {groupItems.map((item) => (
                <NavItemComponent
                  key={item.path}
                  item={item}
                  level={1}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          );
        })}
      {/* Render ungrouped items (excluding root) in Other group */}
      {ungrouped.length > 0 && (
        <div>
          <GroupHeader name="Other" isFirst={groupsRendered === 0} />
          {ungrouped.map((item) => (
            <NavItemComponent
              key={item.path}
              item={item}
              level={1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </nav>
  );
}
